import React, { useRef, useEffect, memo } from "react";
import { Flex, TextArea, Button, Spinner, IconArrowOpenEndLine } from "@smarterservices/smarter-ui";

/**
 * ChatInput component renders the message input form and send button.
 * Memoized to prevent unnecessary re-renders when parent re-renders.
 *
 * @param {object} props
 * @param {string} props.messageText - Current text in the input box.
 * @param {function} props.setMessageText - Setter for message text.
 * @param {string} props.sendError - Error message if send fails.
 * @param {boolean} props.isSubmitting - Loading state for send action.
 * @param {function} props.handleSubmit - Handler for form submit.
 * @returns {JSX.Element}
 */
const ChatInput = memo(({ messageText, setMessageText, sendError, isSubmitting, handleSubmit }) => {
  const formRef = useRef(null);
  const textAreaRef = useRef(null);

  // Styles for input container, textarea, and button placement
  const inputContainerStyle = {
    position: 'relative',
    width: '100%',
    height: '100%',
    backgroundColor: '#fff',

  };
  const textAreaStyle = {
    border: 'none',
    paddingRight: '45px',
    boxSizing: 'border-box',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap'
  };
  const sendBtnContainerStyle = {
    position: 'absolute',
    right: '8px',
    bottom: sendError ? '33px' : '8px',
    zIndex: 2
  };

  useEffect(() => {
    if (!isSubmitting) {
      if (textAreaRef.current && typeof textAreaRef.current.focus === 'function') {
        setTimeout(() => {
          textAreaRef.current.focus();
        }, 50);
      }
    }
  }, [isSubmitting]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(formRef.current);
    const message = formData.get('chatMessage').trim();

    if (!message || isSubmitting) return;

    // Pass the message to the handler
    const success = await handleSubmit(message);

    // Reset the form
    if (success) {
      formRef.current.reset();
    }

  };

  return (
    <form
      ref={formRef}
      onSubmit={handleFormSubmit}
      style={{ width: '100%', height: '100%' }}
    >
      <Flex direction="column" padding="x-small" aria-label="Chat message form" height="100%">
        <div style={inputContainerStyle}>
          <TextArea
            name="chatMessage"
            label=""
            aria-label="Chat message input"
            defaultValue={messageText}
            placeholder="Type a message..."
            disabled={isSubmitting}
            width="100%"
            height="100%"
            resize="none"
            size="small"
            autoGrow={true}
            textareaRef={el => { textAreaRef.current = el; }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleFormSubmit(e);
              }
            }}
            messages={sendError ? [{ type: 'error', text: sendError }] : []}
            style={textAreaStyle}
          />
          <div style={sendBtnContainerStyle}>
            <Button
              type="submit"
              size="small"
              shape="circle"
              disabled={isSubmitting}
              aria-label="Send message"
              renderIcon={
                isSubmitting ?
                  <Spinner size="x-small" renderTitle="Sending message..." /> :
                  <IconArrowOpenEndLine />
              }
            />
          </div>
        </div>
      </Flex>
    </form>
  );
});

// Display name for React DevTools debugging
ChatInput.displayName = 'ChatInput';

export default ChatInput;
